import React, { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import { API_TARGET, getCsrfToken } from "@mhj/shared/api";
import { compressImage, formatBytes } from "@mhj/shared/compress-image";
import { docHint } from "../lib/docs";

/**
 * One document, photographed on the applicant's phone.
 *
 * Compression happens here rather than server-side because the server's limit
 * is 400KB and a phone photo is 2-5MB. Without it nearly every upload from the
 * devices this programme's applicants actually own would fail with "too
 * large", on the last step, with no way to fix it.
 */
export default function DocumentUpload({
  applicationId,
  docType,
  label,
  uploaded,
}: {
  applicationId: string;
  docType: string;
  label: string;
  uploaded: boolean;
}) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const hint = docHint(docType);

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setBusy(true);
    setNote(null);

    try {
      const result = await compressImage(file);
      if (result.wasCompressed) {
        setNote(
          `Shrunk from ${formatBytes(result.originalBytes)} to ${formatBytes(result.compressedBytes)} before sending.`,
        );
      }

      const body = new FormData();
      body.append("applicationId", applicationId);
      body.append("docType", docType);
      body.append("file", result.file);

      // FormData needs the browser to set its own multipart boundary, so this
      // goes through fetch rather than the shared axios client.
      const response = await fetch(`${API_TARGET}/api/empowerment/upload`, {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRF-Token": getCsrfToken() },
        body,
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error ?? "That upload did not go through. Try again.");
      }

      await queryClient.invalidateQueries({ queryKey: ["empowerment", "me"] });
      toast.success(`${label} uploaded`);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      className={`rounded-2xl border p-4 ${
        uploaded ? "border-emerald-300 bg-emerald-50" : "border-slate-300 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-slate-900">{label}</p>
          {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
          {note && <p className="mt-1 text-xs text-emerald-700">{note}</p>}
        </div>
        {uploaded && (
          <span className="shrink-0 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white">
            Added
          </span>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,application/pdf"
        capture="environment"
        className="hidden"
        onChange={(e) => void handleFile(e.target.files?.[0] ?? null)}
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        className="mt-3 w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
      >
        {busy ? "Sending…" : uploaded ? "Replace this photo" : "Take or choose a photo"}
      </button>
    </div>
  );
}
